'use client';

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  JSX,
} from 'react';
import Skeleton from 'react-loading-skeleton';
import { getCookie } from 'cookies-next';
import { usePathname } from 'next/navigation';

// types
import { IHousePlanHit } from '@/lib/search/types';
import {
  IFilterConfig,
  IOrFilteredGridProps,
  SetFilterFn,
  TFilterValues,
} from './or-filtered-grid.types';

// hooks
import { useLocation } from '@/context/locationContext';
import { useFilters } from './hooks/useFilters';
import { useSetLocationModal } from '@/context/setLocationContext';

// utils
import { sendGTMFilterEvent } from '@/components/helpers/analytics/gtm';
import { useGridScrollRestore } from '@/context/gridScrollRestoreContext';

const getSelectedOptionLabel = (
  config: IFilterConfig<any>[],
  key: string,
  value: unknown,
): string => {
  const filter = config.find((c) => c.key === key);
  if (!filter) return String(value);
  if (filter.type === 'select') {
    return (
      filter.options?.find((opt) => opt.value === value)?.label ?? String(value)
    );
  }
  return value ? 'Checked' : 'Unchecked';
};

export const OrFilteredGrid = <T,>({
  config,
  data,
  initialSort,
  isLoading,
  nonFilteredData = [],
  nonFilteredDataIndex = 0,
  renderCard,
  renderControls,
  shouldLazyLoad = false,
  sortOptions,
  visibleCards = data.length,
  restoreKey,
}: IOrFilteredGridProps<T>): JSX.Element => {
  const {
    setVisibleCount: setContextCount,
    visibleCount: contextVisibleCount,
    lastVisitedUid,
    setLastVisitedUid,
    isExpired,
    setGridScroll,
    gridScroll,
  } = useGridScrollRestore();

  // should the scroll position be restored on back navigation
  const shouldRestoreScroll = lastVisitedUid === restoreKey;

  const pathname = usePathname();

  const [visibleCount, setVisibleCount] = useState(
    shouldRestoreScroll && !isExpired ? contextVisibleCount : visibleCards,
  );
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);
  const gridRef = useRef<HTMLDivElement | null>(null);

  const { currentBuildStudio } = useLocation();
  const { handleOpen } = useSetLocationModal();

  // required for accuate GTM tracking
  const lastChangedFilter = useRef<{ key: string; value: unknown } | null>(
    null,
  );
  const lastChangedSort = useRef<{ key: string; value: string } | null>(null);

  const {
    filteredData,
    filterValues,
    resetFilters,
    setFilter,
    sort,
    setSort,
    filteredCount,
  } = useFilters({
    config,
    data,
    initialSort,
    sortOptions,
    designStudioId: Number(currentBuildStudio?.Id),
  });

  const visibleData = useMemo(
    () => (shouldLazyLoad ? filteredData.slice(0, visibleCount) : filteredData),
    [filteredData, shouldLazyLoad, visibleCount],
  );

  const loadMore = useCallback(() => {
    if (isLoadingMore || !shouldLazyLoad) return;
    setIsLoadingMore(true);
    setVisibleCount((prev) => prev + visibleCards);
  }, [isLoadingMore, shouldLazyLoad, visibleCards]);

  useEffect(() => {
    setContextCount(visibleCount);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleCount]);

  const updateResultsAnnouncement = ({
    visible,
    matching,
    total,
  }: {
    visible: number;
    matching: number;
    total: number;
  }) => {
    const el = document.getElementById('results-announcement');
    if (!el) return;

    let message = `${visible} of ${matching} matching results are currently visible.`;
    if (matching !== total) message += ` Out of ${total} total results.`;
    if (visible < matching)
      message += ` Tab through the grid to load more results.`;

    if (el.textContent !== message) el.textContent = message;
  };

  useEffect(() => {
    setIsLoadingMore(false);

    // Fire GTM only when visibleData updates AND we have a known last change
    if (lastChangedFilter.current) {
      const { key, value } = lastChangedFilter.current;
      sendGTMFilterEvent(
        key,
        visibleData.length,
        getSelectedOptionLabel(config, key, value),
      );
      lastChangedFilter.current = null;
    }

    // GTM: Sort
    if (lastChangedSort.current) {
      sendGTMFilterEvent(
        'sort',
        visibleData.length,
        lastChangedSort.current.value,
      );
      lastChangedSort.current = null;
    }

    const timeout = setTimeout(() => {
      updateResultsAnnouncement({
        visible: visibleData.length,
        matching: filteredCount,
        total: data.length,
      });
    }, 250);

    return () => clearTimeout(timeout);
  }, [visibleData, filteredData, config, data.length, filteredCount]);

  // Lazy loading on scroll
  useEffect(() => {
    if (!shouldLazyLoad || isLoadingMore) return;

    const handleScroll = () => {
      const gridBottom = gridRef.current?.getBoundingClientRect().bottom ?? 0;
      const nearBottom = gridBottom - window.innerHeight < 200;
      if (nearBottom) loadMore();
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [shouldLazyLoad, isLoadingMore, loadMore]);

  // Lazy load more when tabbing through cards
  useEffect(() => {
    const nearEnd =
      focusedIndex !== null && focusedIndex >= visibleData.length - 3;
    const hasMore = visibleData.length < filteredData.length;

    if (shouldLazyLoad && !isLoadingMore && nearEnd && hasMore) {
      loadMore();
    }
  }, [
    focusedIndex,
    visibleData.length,
    filteredData.length,
    loadMore,
    shouldLazyLoad,
    isLoadingMore,
  ]);

  // Save scroll position unless we're restoring it
  useEffect(() => {
    const handleScroll = () => {
      if (!shouldRestoreScroll && !isExpired) {
        setGridScroll(window.scrollY);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [shouldRestoreScroll, isExpired, setGridScroll]);

  // Restore scroll if coming back from detail page
  useEffect(() => {
    if (shouldRestoreScroll && gridScroll > 0 && !isLoading) {
      window.scrollTo({ top: gridScroll, behavior: 'auto' });
      setLastVisitedUid(null);
    }
  }, [pathname, shouldRestoreScroll, gridScroll, isLoading, setLastVisitedUid]);

  const handleSort = (newSort: string) => {
    setSort(newSort);
    lastChangedSort.current = { key: 'sort', value: newSort };
  };

  const handleFilter = <K extends keyof TFilterValues>(
    key: K,
    value: TFilterValues[K],
  ) => {
    setFilter(key, value);
    lastChangedFilter.current = { key, value };
  };

  const handleReset = () => {
    setVisibleCount(visibleCards);
    setFocusedIndex(null);
    resetFilters();
    sendGTMFilterEvent('reset', visibleCards, '');
  };

  // Show location modal if no location is set
  const [clientReady, setClientReady] = useState(false);

  useEffect(() => {
    setClientReady(true);
  }, []);

  useEffect(() => {
    if (!clientReady) return;
    const studioIdCookie = getCookie('studioId');
    if (!studioIdCookie && !currentBuildStudio?.Id) {
      handleOpen();
    }
  }, [clientReady, currentBuildStudio?.Id, handleOpen]);

  const totalFromStudio = data.filter(
    (homes) => (homes as IHousePlanHit)?.FloorPlans?.[0]?.IsPreferred,
  );

  return (
    <div data-theme="light">
      <div
        ref={gridRef}
        className="container"
        aria-describedby="results-announcement"
      >
        <div
          className="sr-only"
          role="status"
          aria-live="polite"
          aria-atomic="true"
          id="results-announcement"
        />

        {/* controls */}
        {config &&
          renderControls?.({
            config,
            filterValues,
            setFilter: handleFilter,
            resetFilters: handleReset,
            filteredResults: filteredCount,
            totalResults: totalFromStudio.length,
            sort,
            setSort: handleSort,
            sortOptions,
          })}

        {isLoading ? (
          <Skeleton
            baseColor="var(--color-neutral-100)"
            highlightColor="var(--color-neutral-200)"
            height={200}
            className="rounded-30"
          />
        ) : (
          <div className="mt-10 grid w-full grid-cols-1 gap-y-8 md:grid-cols-2 md:gap-x-8 md:gap-y-16 xl:grid-cols-3">
            {/* Pre-nonFilteredData */}
            {visibleData.slice(0, nonFilteredDataIndex).map((item, i) =>
              renderCard(item, currentBuildStudio?.Id, {
                onFocus: () => setFocusedIndex(i),
              }),
            )}

            {/* Non-filtered items like promos */}
            {nonFilteredData.map((item, i) => (
              <React.Fragment key={`non-${i}`}>
                {renderCard(item)}
              </React.Fragment>
            ))}

            {/* Post-nonFilteredData */}
            {visibleData.slice(nonFilteredDataIndex).map((item, i) => {
              const index = i + nonFilteredDataIndex;
              return (
                <React.Fragment key={index}>
                  {renderCard(item, currentBuildStudio?.Id, {
                    onFocus: () => setFocusedIndex(index),
                  })}
                </React.Fragment>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
